"use client";
import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  type AddQuoteResult,
  type BabyEntryPage,
  BabyInsuredParty,
  type BabyQuote,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { ComposedDatePicker } from "@/components/shared/composedDatePicker/ComposedDatePicker";
import {
  ComposedRadioButton,
  ComposedRadioButtonValue,
} from "@/components/shared/composedRadioButton/ComposedRadioButton";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useEntry } from "@/hooks/useEntry";
import { BabyEntryValidationSchema } from "@/validators/products/baby/BabyEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: BabyEntryPage | null;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: BabyQuote) => Promise<AddQuoteResult>;
};

export const BabyEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
}: EntryProps): React.JSX.Element => {
  const {
    currentUser,
    currentPolicyHolder,
    content: babyEntryPage,
    isLoading,
    submitHandler,
  } = useEntry<BabyQuote, BabyEntryPage>({
    externalUser,
    externalSubmitHandler,
    externalIsLoading,
    externalContent,
    externalPolicyHolder,
    contentKey: "babyEntryPage",
  });

  const defaultValues: BabyQuote = {
    insuredParty: BabyInsuredParty.POLICY_HOLDER,
    productType: ProductType.BABY,
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
  };
  const { formState, control, handleSubmit, watch } = useForm<BabyQuote>({
    defaultValues,
    resolver: yupResolver(BabyEntryValidationSchema(babyEntryPage)),
    disabled: isLoading,
  });

  const errors = formState.errors;
  const insuredParty = watch("insuredParty");

  const isMother = insuredParty === BabyInsuredParty.POLICY_HOLDER;

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form
          onSubmit={handleSubmit(submitHandler)}
          noValidate
        >
          <Controller
            name={"insuredParty"}
            control={control}
            render={({ field }) => (
              <ComposedRadioButton
                className="if flex-column"
                id={field.name}
                aria-describedby="insured-party-description"
                errorMessage={errors.insuredParty?.message}
                invalid={undefined}
                label={babyEntryPage?.insuredParty?.label?.value}
                disabled={isLoading}
                {...field}
                value={field?.value ?? ""}
                popoverMessage={babyEntryPage?.insuredParty?.popover?.value}
              >
                <ComposedRadioButtonValue
                  defaultChecked={
                    field.value === BabyInsuredParty.POLICY_HOLDER
                  }
                  name={field.name}
                  data-testid="policy-holder-policy-holder-radio"
                  id={`${field.name}-${BabyInsuredParty.POLICY_HOLDER.toLowerCase()}`}
                  label={babyEntryPage?.insuredPartyIsMother?.value}
                  value={BabyInsuredParty.POLICY_HOLDER}
                  disabled={isLoading}
                />
                <ComposedRadioButtonValue
                  defaultChecked={field.value === BabyInsuredParty.MOTHER}
                  name={field.name}
                  data-testid="insured-party-mother-radio"
                  id={`${field.name}-${BabyInsuredParty.MOTHER.toLowerCase()}`}
                  label={babyEntryPage?.insuredPartyIsNotMother?.value}
                  value={BabyInsuredParty.MOTHER}
                  disabled={isLoading}
                />
              </ComposedRadioButton>
            )}
          />

          <Controller
            name={"policyHolderSsn"}
            control={control}
            render={({ field }) => (
              <SsnInput
                id={field.name}
                placeholder={
                  babyEntryPage?.entryPageCommon?.ssnQuestion?.placeholder
                    ?.value ?? undefined
                }
                data-testid="policy-holder-ssn-input"
                errorMessage={errors.policyHolderSsn?.message}
                disabled={
                  isLoading ||
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated
                }
                popoverMessage={
                  babyEntryPage?.entryPageCommon?.ssnQuestion?.popover?.value ??
                  undefined
                }
                {...field}
              >
                {babyEntryPage?.entryPageCommon?.ssnQuestion?.label?.value}
              </SsnInput>
            )}
          />

          {!isMother && (
            <Controller
              name={"babyMother.ssn"}
              control={control}
              render={({ field }) => (
                <SsnInput
                  id={field.name}
                  data-testid="baby-mother-ssn-input"
                  placeholder={
                    babyEntryPage?.babyMotherSsn?.placeholder?.value ??
                    undefined
                  }
                  errorMessage={errors.babyMother?.ssn?.message}
                  disabled={isLoading || isMother}
                  popoverMessage={
                    babyEntryPage?.babyMotherSsn?.popover?.value ?? undefined
                  }
                  {...field}
                >
                  {babyEntryPage?.babyMotherSsn?.label?.value}
                </SsnInput>
              )}
            />
          )}

          <Controller
            name={"expectedDateOfBirth"}
            control={control}
            render={({ field }) => (
              <ComposedDatePicker
                id={field.name}
                placeholder={
                  babyEntryPage?.expectedDateOfBirth?.placeholder?.value ??
                  undefined
                }
                data-testid="baby-expected-date-of-birth-date-picker"
                errorMessage={errors.expectedDateOfBirth?.message}
                disabled={isLoading}
                popoverMessage={
                  babyEntryPage?.expectedDateOfBirth?.popover?.value
                }
                {...field}
              >
                {babyEntryPage?.expectedDateOfBirth?.label?.value}
              </ComposedDatePicker>
            )}
          />
          <ProductContainer.CallToAction
            htmlType="submit"
            data-testid="baby-entry-submit-button"
            disabled={isLoading}
            loading={isLoading}
          >
            {babyEntryPage?.entryPageCommon?.submitButtonText?.value}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
